import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../hooks/redux'
import {
  fetchContacts,
  createContact,
  updateContact,
  deleteContact,
  setActiveContact,
} from '../../store/slices/financeSlice'
import type { Contact, ContactCreateInput, ContactUpdateInput, ContactRole, Unit } from '@/types/finance'
import { useToast } from '../../components/ToastContainer'
import { useFinancePageSize, FINANCE_PAGE_SIZE_OPTIONS } from '../../hooks/useFinancePageSize'
import { addUnitContact, removeUnitContact, getContact, getUnits, exportContacts, exportContactOwners } from '../../services/api/finance'
import usePermissions, { Permission } from '../../hooks/usePermissions'
import { FinanceAuditLog } from '../../components/finance'
import {
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HomeIcon,
  TruckIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'

interface ContactFormData {
  name: string
  idNumber: string
  email: string
  phone: string
  lineId: string
  bankCode: string
  bankAccount: string
  ownerMeetingPostalCode: string
  ownerMeetingAddress: string
  note: string
}

const initialFormData: ContactFormData = {
  name: '',
  idNumber: '',
  email: '',
  phone: '',
  lineId: '',
  bankCode: '',
  bankAccount: '',
  ownerMeetingPostalCode: '',
  ownerMeetingAddress: '',
  note: '',
}

// 依聯絡人的 UnitContact 聚合角色標籤（例如「所有權人×2」、「租客×1」）
const ROLE_LABELS: Record<string, { label: string; className: string }> = {
  OWNER: { label: '所有權人', className: 'bg-indigo-100 text-indigo-800' },
  TENANT: { label: '租客', className: 'bg-amber-100 text-amber-800' },
  FAMILY: { label: '家人', className: 'bg-green-100 text-green-800' },
  OTHER: { label: '其他', className: 'bg-gray-100 text-gray-700' },
}
const ROLE_OPTIONS: ContactRole[] = ['OWNER', 'TENANT', 'FAMILY', 'OTHER']

const ContactList = () => {
  const { projectKey } = useParams<{ projectKey: string }>()
  const dispatch = useAppDispatch()
  const { showSuccess, showError } = useToast()
  const { currentProject } = useAppSelector(state => state.project)
  const {
    contacts,
    contactsPagination,
    contactsLoading,
    contactsError,
    activeContact,
  } = useAppSelector(state => state.finance)

  const { hasPermission } = usePermissions(currentProject?.id)
  const canManage = hasPermission(Permission.MANAGE_FINANCE_CONTACTS)
  const canDelete = hasPermission(Permission.DELETE_FINANCE_CONTACTS)
  const canView = hasPermission(Permission.VIEW_FINANCE_CONTACTS)

  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<'name' | 'unit'>('name')
  const [hasOwnerMeetingAddress, setHasOwnerMeetingAddress] = useState(false)
  const [emailPayerOnly, setEmailPayerOnly] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useFinancePageSize()
  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<ContactFormData>(initialFormData)
  const [submitting, setSubmitting] = useState(false)
  const [justCreated, setJustCreated] = useState(false) // 建立後停在關聯編輯步驟
  // 聯絡人 × 戶別 關聯管理（嵌入編輯 modal）
  const [unitsContact, setUnitsContact] = useState<Contact | null>(null)
  const [unitsContactLoading, setUnitsContactLoading] = useState(false)
  const [allUnits, setAllUnits] = useState<Unit[]>([])
  const [unitSearch, setUnitSearch] = useState('')
  const [newUnitId, setNewUnitId] = useState('')
  const [newUnitRole, setNewUnitRole] = useState<ContactRole>('OWNER')
  const [unitMutating, setUnitMutating] = useState(false)
  const [exportMenuOpen, setExportMenuOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    if (currentProject?.id) {
      dispatch(fetchContacts({
        projectId: currentProject.id,
        params: {
          page,
          limit: pageSize,
          search: search || undefined,
          sort,
          hasOwnerMeetingAddress: hasOwnerMeetingAddress || undefined,
          emailPayerOnly: emailPayerOnly || undefined,
        },
      }))
    }
  }, [dispatch, currentProject?.id, page, pageSize, search, sort, hasOwnerMeetingAddress, emailPayerOnly])

  const handleSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  const renderPaginationBar = (className?: string) => {
    if (!contactsPagination) return null
    return (
      <div className={`px-4 py-2 flex items-center justify-between ${className || ''}`}>
        <div className="flex items-center space-x-3">
          <div className="text-sm text-gray-700">
            共 {contactsPagination.total} 筆
            {contactsPagination.totalPages > 1 && (
              <>，第 {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, contactsPagination.total)} 筆</>
            )}
          </div>
          <div className="flex items-center space-x-1">
            <label className="text-sm text-gray-500">每頁</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setPage(1)
              }}
              className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              {FINANCE_PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="text-sm text-gray-500">筆</span>
          </div>
        </div>
        {contactsPagination.totalPages > 1 && (
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">
              {contactsPagination.page} / {contactsPagination.totalPages} 頁
            </span>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeftIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setPage(p => Math.min(contactsPagination.totalPages, p + 1))}
              disabled={page === contactsPagination.totalPages}
              className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRightIcon className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    )
  }

  const triggerBlobDownload = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const handleExport = async (mode: 'all' | 'owners' | 'owners-only') => {
    if (!currentProject?.id || exporting) return
    setExporting(true)
    setExportMenuOpen(false)
    const today = new Date().toISOString().slice(0, 10)
    const filePrefix = projectKey || currentProject.key || currentProject.name || 'project'
    try {
      if (mode === 'all') {
        const blob = await exportContacts(currentProject.id, {
          ...(hasOwnerMeetingAddress ? { hasOwnerMeetingAddress: true } : {}),
          ...(emailPayerOnly ? { emailPayerOnly: true } : {}),
        })
        triggerBlobDownload(blob, `${filePrefix}_聯絡人_${today}.xlsx`)
        showSuccess('匯出成功', '聯絡人 Excel 已下載')
      } else {
        const excludeNoOwner = mode === 'owners-only'
        const blob = await exportContactOwners(currentProject.id, { excludeNoOwner })
        const suffix = excludeNoOwner ? '_排除空戶' : ''
        triggerBlobDownload(blob, `${filePrefix}_區權人名冊${suffix}_${today}.xlsx`)
        showSuccess('匯出成功', '區權人名冊 Excel 已下載')
      }
    } catch (error: any) {
      showError('匯出失敗', error.message || '請稍後再試')
    } finally {
      setExporting(false)
    }
  }

  const openCreateModal = () => {
    setFormData(initialFormData)
    setIsEditing(false)
    setShowModal(true)
  }

  const openEditModal = async (contact: Contact) => {
    setFormData({
      name: contact.name,
      idNumber: contact.idNumber || '',
      email: contact.email || '',
      phone: contact.phone || '',
      lineId: contact.lineId || '',
      bankCode: contact.bankCode || '',
      bankAccount: contact.bankAccount || '',
      ownerMeetingPostalCode: contact.ownerMeetingPostalCode || '',
      ownerMeetingAddress: contact.ownerMeetingAddress || '',
      note: contact.note || '',
    })
    dispatch(setActiveContact(contact))
    setIsEditing(true)
    setShowModal(true)
    // 同步載入關聯戶別資料，讓 modal 底部可直接編輯
    setUnitsContact(contact)
    setUnitSearch('')
    setNewUnitId('')
    setNewUnitRole('OWNER')
    await loadContactWithUnits(contact.id)
    if (currentProject?.id && allUnits.length === 0) {
      try {
        const res = await getUnits(currentProject.id, { page: 1, limit: 2000 })
        if (res.success && res.data) setAllUnits(res.data.units)
      } catch (error: any) {
        showError('載入失敗', error.message || '無法載入戶別清單')
      }
    }
  }

  const closeModal = () => {
    setShowModal(false)
    dispatch(setActiveContact(null))
    setFormData(initialFormData)
    setJustCreated(false)
    // 關閉時也清掉 modal 底部的關聯狀態
    setUnitsContact(null)
    setUnitSearch('')
    setNewUnitId('')
    setNewUnitRole('OWNER')
  }

  // ===== 聯絡人 × 戶別 關聯管理 =====
  const loadContactWithUnits = async (contactId: string) => {
    if (!currentProject?.id) return
    setUnitsContactLoading(true)
    try {
      const res = await getContact(currentProject.id, contactId)
      if (res.success && res.data) {
        setUnitsContact(res.data.contact)
      }
    } catch (error: any) {
      showError('載入失敗', error.message || '無法載入聯絡人資料')
    } finally {
      setUnitsContactLoading(false)
    }
  }

  const handleAddContactUnit = async () => {
    if (!currentProject?.id || !unitsContact) return
    if (!newUnitId) {
      showError('請選擇戶別')
      return
    }
    const already = unitsContact.unitContacts?.some(
      uc => uc.unitId === newUnitId && uc.role === newUnitRole
    )
    if (already) {
      showError('此戶別已有相同角色的關聯')
      return
    }
    setUnitMutating(true)
    try {
      await addUnitContact(currentProject.id, newUnitId, {
        contactId: unitsContact.id,
        role: newUnitRole,
      })
      showSuccess('已新增關聯')
      await loadContactWithUnits(unitsContact.id)
      setNewUnitId('')
      setUnitSearch('')
      // 刷新聯絡人列表以更新角色聚合
      if (currentProject?.id) {
        dispatch(fetchContacts({
          projectId: currentProject.id,
          params: { page, limit: pageSize, search: search || undefined },
        }))
      }
    } catch (error: any) {
      showError('新增失敗', error.message || '請稍後再試')
    } finally {
      setUnitMutating(false)
    }
  }

  const handleRemoveContactUnit = async (unitContactId: string, unitId: string, unitCode?: string) => {
    if (!currentProject?.id || !unitsContact) return
    if (!confirm(`確定要移除與戶別「${unitCode || unitId}」的關聯嗎？`)) return
    setUnitMutating(true)
    try {
      await removeUnitContact(currentProject.id, unitId, unitContactId)
      showSuccess('已移除關聯')
      await loadContactWithUnits(unitsContact.id)
      if (currentProject?.id) {
        dispatch(fetchContacts({
          projectId: currentProject.id,
          params: { page, limit: pageSize, search: search || undefined },
        }))
      }
    } catch (error: any) {
      showError('移除失敗', error.message || '請稍後再試')
    } finally {
      setUnitMutating(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentProject?.id) return

    if (!formData.name.trim()) {
      showError('請輸入姓名')
      return
    }

    setSubmitting(true)
    try {
      if (isEditing && activeContact) {
        // 編輯時空字串送 null 才能清空後端值（送 undefined 會被 controller 略過不更新）
        const emptyToNull = (v: string) => (v.trim() === '' ? null : v.trim())
        const updateData: ContactUpdateInput = {
          name: formData.name,
          idNumber: emptyToNull(formData.idNumber),
          email: emptyToNull(formData.email),
          phone: emptyToNull(formData.phone),
          lineId: emptyToNull(formData.lineId),
          bankCode: emptyToNull(formData.bankCode),
          bankAccount: emptyToNull(formData.bankAccount),
          ownerMeetingPostalCode: emptyToNull(formData.ownerMeetingPostalCode),
          ownerMeetingAddress: emptyToNull(formData.ownerMeetingAddress),
          note: emptyToNull(formData.note),
        }
        await dispatch(updateContact({
          projectId: currentProject.id,
          contactId: activeContact.id,
          data: updateData,
        })).unwrap()
        showSuccess('聯絡人更新成功')
      } else {
        const createData: ContactCreateInput = {
          name: formData.name,
          idNumber: formData.idNumber || undefined,
          email: formData.email || undefined,
          phone: formData.phone || undefined,
          lineId: formData.lineId || undefined,
          bankCode: formData.bankCode || undefined,
          bankAccount: formData.bankAccount || undefined,
          ownerMeetingPostalCode: formData.ownerMeetingPostalCode || undefined,
          ownerMeetingAddress: formData.ownerMeetingAddress || undefined,
          note: formData.note || undefined,
        }
        const created = await dispatch(createContact({
          projectId: currentProject.id,
          data: createData,
        })).unwrap()
        showSuccess('聯絡人建立成功，可繼續新增關聯戶別')
        // 第二步：切換成編輯模式，載入關聯並保持 modal 開啟讓使用者新增戶別關聯
        if (created?.id) {
          setIsEditing(true)
          setJustCreated(true)
          dispatch(setActiveContact(created))
          setUnitsContact(created)
          setUnitSearch('')
          setNewUnitId('')
          setNewUnitRole('OWNER')
          await loadContactWithUnits(created.id)
          if (currentProject?.id && allUnits.length === 0) {
            try {
              const res = await getUnits(currentProject.id, { page: 1, limit: 2000 })
              if (res.success && res.data) setAllUnits(res.data.units)
            } catch (error: any) {
              showError('載入失敗', error.message || '無法載入戶別清單')
            }
          }
        } else {
          closeModal()
        }
        return
      }
      closeModal()
    } catch (error: any) {
      showError(isEditing ? '更新失敗' : '建立失敗', error.message || '請稍後再試')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (contact: Contact) => {
    if (!currentProject?.id) return
    if (!confirm(`確定要刪除聯絡人「${contact.name}」嗎？`)) return

    try {
      await dispatch(deleteContact({
        projectId: currentProject.id,
        contactId: contact.id,
      })).unwrap()
      showSuccess('聯絡人已刪除')
    } catch (error: any) {
      const msg = typeof error === 'string' ? error : (error?.message || '請稍後再試')
      showError('刪除失敗', msg)
    }
  }

  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto mt-12 text-center bg-white rounded-lg shadow">
        <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">無權限存取</h2>
        <p className="mt-2 text-sm text-gray-600">您沒有檢視聯絡人管理的權限，請聯絡專案管理員。</p>
        <Link to={`/project/${projectKey}/finance`} className="inline-block mt-4 text-blue-600 hover:underline">回財務管理首頁</Link>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/project/${projectKey}/finance`}
            className="text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <div className="flex items-center space-x-3">
            <UserGroupIcon className="w-8 h-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">聯絡人管理</h1>
              <p className="text-sm text-gray-500">管理住戶與繳費人資料</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {canView && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setExportMenuOpen(v => !v)}
                disabled={exporting}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowDownTrayIcon className="w-4 h-4 mr-2" />
                {exporting ? '匯出中...' : '匯出'}
                <ChevronDownIcon className="w-4 h-4 ml-1" />
              </button>
              {exportMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setExportMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20">
                    <button
                      type="button"
                      onClick={() => handleExport('all')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      匯出全部聯絡人
                      <div className="text-xs text-gray-500 mt-0.5">含全欄位（依目前篩選）</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExport('owners')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                    >
                      匯出區權人名冊
                      <div className="text-xs text-gray-500 mt-0.5">含全部戶號，沒有區權人的空戶也列出</div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExport('owners-only')}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border-t border-gray-100"
                    >
                      匯出區權人名冊（排除空戶）
                      <div className="text-xs text-gray-500 mt-0.5">只匯出有區權人的戶號</div>
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {canManage && (
            <button
              onClick={openCreateModal}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              新增聯絡人
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="搜尋姓名、電話、Email、身分證、關聯戶號..."
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2 whitespace-nowrap">
            <label htmlFor="contactSort" className="text-sm text-gray-600">排序</label>
            <select
              id="contactSort"
              value={sort}
              onChange={(e) => { setSort(e.target.value as 'name' | 'unit'); setPage(1) }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="name">姓名</option>
              <option value="unit">戶別</option>
            </select>
          </div>
        </div>
        <div className="mt-3 flex flex-col gap-2">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={hasOwnerMeetingAddress}
              onChange={(e) => { setHasOwnerMeetingAddress(e.target.checked); setPage(1) }}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            只顯示有填寫「區權人大會郵寄地址」的聯絡人
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={emailPayerOnly}
              onChange={(e) => { setEmailPayerOnly(e.target.checked); setPage(1) }}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            只顯示有 Email 且關聯戶別繳費方式為 Email 的聯絡人
          </label>
        </div>
      </div>

      {/* Top Pagination Bar */}
      {!contactsLoading && renderPaginationBar('bg-white rounded-lg shadow')}

      {contactsError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-700">{contactsError}</p>
        </div>
      )}

      {/* Contacts Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {contactsLoading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : contacts.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {search ? '找不到符合條件的聯絡人' : '尚無聯絡人資料'}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      姓名
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      電話
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                      ACH
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[240px]">
                      關聯戶別 / 角色
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      關聯車位
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[220px]">
                      區權人大會郵寄地址
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[180px]">
                      Email
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-10 shadow-[-4px_0_4px_-2px_rgba(0,0,0,0.06)]">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.map((contact) => (
                    <tr key={contact.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {contact.name}
                        </div>
                        {contact.idNumber && (
                          <div className="text-xs text-gray-500">{contact.idNumber}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {contact.phone || '-'}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-center w-16">
                        {contact.bankCode && contact.bankAccount ? (
                          <span
                            className="inline-block"
                            title={`ACH 銀行代碼 ${contact.bankCode}｜帳號 ${contact.bankAccount}`}
                          >
                            <CheckCircleIcon className="w-5 h-5 text-green-600" />
                          </span>
                        ) : (
                          <span className="text-gray-300 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {(() => {
                          // 合併「戶別 × 角色」一筆一行，另外加上「繳費人」(從 payerForUnits)
                          // 若該聯絡人本身已是該戶別的 OWNER，則不重複顯示繳費人 tag
                          type Line = { unitCode: string; label: string; className: string; lease?: string; expired?: boolean }
                          const lines: Line[] = []
                          const today = new Date()
                          today.setHours(0, 0, 0, 0)
                          const ownedUnitIds = new Set(
                            (contact.unitContacts || [])
                              .filter(uc => uc.role === 'OWNER' && uc.unit?.id)
                              .map(uc => uc.unit!.id)
                          )
                          contact.unitContacts?.forEach(uc => {
                            if (!uc.unit) return
                            const role = ROLE_LABELS[uc.role] || { label: uc.role, className: 'bg-gray-100 text-gray-700' }
                            let lease: string | undefined
                            let expired = false
                            if (uc.role === 'TENANT' && uc.leaseStartDate && uc.leaseEndDate) {
                              const endDate = new Date(String(uc.leaseEndDate).slice(0, 10))
                              expired = endDate < today
                              lease = `${String(uc.leaseStartDate).slice(0, 10)} ~ ${String(uc.leaseEndDate).slice(0, 10)}`
                            }
                            lines.push({ unitCode: uc.unit.unitCode, label: role.label, className: role.className, lease, expired })
                          })
                          contact.payerForUnits?.forEach(unit => {
                            if (ownedUnitIds.has(unit.id)) return // 同人身兼所有權人，不重複列繳費人
                            lines.push({ unitCode: unit.unitCode, label: '繳費人', className: 'bg-blue-100 text-blue-800' })
                          })
                          if (lines.length === 0) return <span className="text-gray-400">-</span>
                          return (
                            <div className="space-y-0.5">
                              {lines.map((ln, idx) => (
                                <div key={idx} className="flex items-center space-x-1.5 whitespace-nowrap">
                                  <span className="font-mono text-sm text-gray-900">{ln.unitCode}</span>
                                  <span className="text-gray-400">-</span>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ln.expired ? 'bg-red-100 text-red-700 line-through' : ln.className}`}>
                                    {ln.label}
                                    {ln.expired && ' (已過期)'}
                                  </span>
                                  {ln.lease && (
                                    <span className={`text-xs ${ln.expired ? 'text-red-500' : 'text-gray-500'}`}>{ln.lease}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {(() => {
                          // 收集所有關聯車位（所有人 + 使用人）
                          const spaceMap = new Map<string, { spaceCode: string; type: string; roles: string[]; licensePlates: string[] }>()

                          contact.ownedSpaces?.forEach(sp => {
                            const existing = spaceMap.get(sp.id)
                            if (existing) {
                              if (!existing.roles.includes('所有人')) existing.roles.push('所有人')
                            } else {
                              spaceMap.set(sp.id, {
                                spaceCode: sp.spaceCode,
                                type: sp.type,
                                roles: ['所有人'],
                                licensePlates: sp.licensePlates || []
                              })
                            }
                          })

                          contact.usedSpaces?.forEach(sp => {
                            const existing = spaceMap.get(sp.id)
                            if (existing) {
                              if (!existing.roles.includes('使用人')) existing.roles.push('使用人')
                            } else {
                              spaceMap.set(sp.id, {
                                spaceCode: sp.spaceCode,
                                type: sp.type,
                                roles: ['使用人'],
                                licensePlates: sp.licensePlates || []
                              })
                            }
                          })

                          if (spaceMap.size === 0) return '-'

                          const typeLabel = (t: string) =>
                            t === 'CAR' ? '汽' : t === 'MOTOR' ? '機' : '腳'

                          return (
                            <div className="flex flex-wrap gap-1">
                              {Array.from(spaceMap.values()).map((item, idx) => (
                                <span
                                  key={idx}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                    item.roles.includes('所有人')
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-yellow-100 text-yellow-800'
                                  }`}
                                  title={`${item.roles.join('、')}${item.licensePlates.length > 0 ? ` | 車牌: ${item.licensePlates.join(', ')}` : ''}`}
                                >
                                  <TruckIcon className="w-3 h-3 mr-1" />
                                  {item.spaceCode}
                                  <span className="ml-0.5 text-[10px] opacity-70">({typeLabel(item.type)})</span>
                                </span>
                              ))}
                            </div>
                          )
                        })()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 align-top">
                        {contact.ownerMeetingAddress ? (
                          <div
                            className="whitespace-pre-wrap break-words max-w-xs line-clamp-3"
                            title={`${contact.ownerMeetingPostalCode ? contact.ownerMeetingPostalCode + ' ' : ''}${contact.ownerMeetingAddress}`}
                          >
                            {contact.ownerMeetingPostalCode && (
                              <span className="font-mono text-gray-500 mr-1">
                                {contact.ownerMeetingPostalCode}
                              </span>
                            )}
                            {contact.ownerMeetingAddress}
                          </div>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 align-top">
                        {contact.email ? (
                          <div className="break-all max-w-xs" title={contact.email}>
                            {contact.email}
                          </div>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium sticky right-0 bg-white z-10 shadow-[-4px_0_4px_-2px_rgba(0,0,0,0.06)]">
                        {canManage && (
                          <button
                            onClick={() => openEditModal(contact)}
                            className="text-blue-600 hover:text-blue-900 mr-3"
                            title="編輯"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(contact)}
                            className="text-red-600 hover:text-red-900"
                            title="刪除"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Bottom Pagination */}
            {renderPaginationBar('border-t border-gray-200')}
          </>
        )}
      </div>

      {/* 聯絡人操作紀錄 */}
      <FinanceAuditLog section="contacts" title="聯絡人操作紀錄" limit={50} />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {justCreated
                  ? `新增聯絡人（步驟 2/2：關聯戶別） - ${activeContact?.name || ''}`
                  : isEditing
                    ? `編輯聯絡人${activeContact ? ` - ${activeContact.name}` : ''}`
                    : '新增聯絡人（步驟 1/2：基本資料）'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  姓名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="請輸入姓名"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  身分證/統編
                </label>
                <input
                  type="text"
                  value={formData.idNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, idNumber: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="A123456789"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    電話
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0912345678"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Line ID
                  </label>
                  <input
                    type="text"
                    value={formData.lineId}
                    onChange={(e) => setFormData(prev => ({ ...prev, lineId: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="line_id"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="email@example.com"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ACH銀行代碼
                  </label>
                  <input
                    type="text"
                    value={formData.bankCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, bankCode: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="004"
                    maxLength={3}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ACH銀行帳號
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccount}
                    onChange={(e) => setFormData(prev => ({ ...prev, bankAccount: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="12345678901234"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  區權人大會郵寄地址
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.ownerMeetingPostalCode}
                    onChange={(e) => setFormData(prev => ({ ...prev, ownerMeetingPostalCode: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center font-mono"
                    placeholder="郵遞區號"
                    maxLength={6}
                    inputMode="numeric"
                    title="郵遞區號（3 / 5 / 6 碼）"
                  />
                  <input
                    type="text"
                    value={formData.ownerMeetingAddress}
                    onChange={(e) => setFormData(prev => ({ ...prev, ownerMeetingAddress: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="區分所有權人會議通知寄送地址（如與居住地不同請填寫）"
                    maxLength={500}
                  />
                </div>
              </div>

              {/* 編輯模式下顯示關聯戶別管理區塊 */}
              {isEditing && unitsContact && (
                <div className="border-t pt-4 space-y-3">
                  <h4 className="text-sm font-medium text-gray-900">關聯戶別 / 角色</h4>

                  <div className="border border-gray-200 rounded-md">
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500">
                      目前關聯（{unitsContact.unitContacts?.length || 0} 筆）
                    </div>
                    {unitsContactLoading ? (
                      <div className="p-4 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                      </div>
                    ) : !unitsContact.unitContacts || unitsContact.unitContacts.length === 0 ? (
                      <div className="p-4 text-sm text-gray-500 text-center">尚無關聯戶別</div>
                    ) : (
                      <div className="divide-y divide-gray-200 max-h-48 overflow-y-auto">
                        {unitsContact.unitContacts.map(uc => (
                          <div key={uc.id} className="flex items-center justify-between px-3 py-2">
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ROLE_LABELS[uc.role]?.className || 'bg-gray-100'}`}>
                                {ROLE_LABELS[uc.role]?.label || uc.role}
                              </span>
                              <HomeIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">{uc.unit?.unitCode || uc.unitId}</span>
                              {uc.unit?.building && (
                                <span className="text-xs text-gray-500">{uc.unit.building} / {uc.unit.floor}</span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveContactUnit(uc.id, uc.unitId, uc.unit?.unitCode)}
                              disabled={unitMutating}
                              className="text-red-500 hover:text-red-700 disabled:opacity-50"
                              title="移除此關聯"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="border border-gray-200 rounded-md">
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500">新增關聯</div>
                    <div className="p-3 space-y-3">
                      <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">戶別</label>
                          <input
                            type="text"
                            value={unitSearch}
                            onChange={(e) => { setUnitSearch(e.target.value); setNewUnitId('') }}
                            placeholder="輸入戶號/地址搜尋..."
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          {unitSearch && (
                            <div className="mt-1 max-h-40 overflow-y-auto border border-gray-200 rounded-md bg-white">
                              {allUnits
                                .filter(u => {
                                  const kw = unitSearch.toLowerCase()
                                  return (
                                    u.unitCode.toLowerCase().includes(kw) ||
                                    u.address.toLowerCase().includes(kw) ||
                                    u.building.toLowerCase().includes(kw)
                                  )
                                })
                                .slice(0, 20)
                                .map(u => (
                                  <button
                                    key={u.id}
                                    type="button"
                                    onClick={() => { setNewUnitId(u.id); setUnitSearch(u.unitCode) }}
                                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 ${newUnitId === u.id ? 'bg-blue-100' : ''}`}
                                  >
                                    <span className="font-medium">{u.unitCode}</span>
                                    <span className="ml-2 text-xs text-gray-500">{u.address}</span>
                                  </button>
                                ))}
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">角色</label>
                          <select
                            value={newUnitRole}
                            onChange={(e) => setNewUnitRole(e.target.value as ContactRole)}
                            className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            {ROLE_OPTIONS.map(r => (
                              <option key={r} value={r}>{ROLE_LABELS[r]?.label || r}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={handleAddContactUnit}
                          disabled={!newUnitId || unitMutating}
                          className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                          {unitMutating ? '處理中...' : '新增關聯'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  備註
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="自由輸入備註資訊..."
                  maxLength={2000}
                />
              </div>

              {/* 編輯紀錄（僅編輯模式） */}
              {isEditing && activeContact && (
                <FinanceAuditLog entityType="Contact" entityId={activeContact.id} compact limit={30} />
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  {justCreated ? '完成' : '取消'}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? '處理中...' : isEditing ? '更新' : '建立並繼續'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  )
}

export default ContactList
